import type { ComponentType } from 'react'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import {
  LuRoute as Route,
  LuClock as Clock,
  LuGauge as Gauge,
  LuZap as Zap,
  LuCircleStop as CircleStop,
  LuPause as Pause,
  LuTrafficCone as TrafficCone,
  LuMountain as Mountain,
} from 'react-icons/lu'
import { themeColors } from '../../../../theme'
import { formatDistance, formatDuration, type TripStats } from '../../../../lib/trackAnalytics'
import { CARD_SHADOW, CARD_SHADOW_HOVER } from './cardStyles'

const MotionBox = motion.create(Box)
const colors = themeColors

interface Tile {
  label: string
  value: string
  unit?: string
  sub?: string
  icon: ComponentType<{ size?: number; color?: string }>
  accent: string
}

function KpiTile({ tile, index }: { tile: Tile; index: number }) {
  const Icon = tile.icon
  return (
    <MotionBox
      bg={colors.bgPanel}
      border="1px solid"
      borderColor={colors.border}
      borderRadius="2xl"
      pt="22px"
      px={5}
      pb={5}
      boxShadow={CARD_SHADOW}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      whileHover={{ y: -3 }}
      _hover={{ boxShadow: CARD_SHADOW_HOVER, borderColor: `${tile.accent}55` }}
      position="relative"
      overflow="hidden"
      minH="118px"
    >
      {/* Barra de acento superior */}
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={tile.accent} />

      {/* Icono de fondo (marca de agua) */}
      <Box position="absolute" right="-10px" bottom="-16px" color={tile.accent} opacity={0.13} pointerEvents="none">
        <Icon size={94} />
      </Box>

      {/* Contenido encima */}
      <Box position="relative" zIndex={1}>
        <Text
          fontSize="10px"
          textTransform="uppercase"
          letterSpacing="0.14em"
          fontWeight="bold"
          color={colors.textMuted}
          lineHeight="1.3"
          mb={2.5}
        >
          {tile.label}
        </Text>
        <Flex alignItems="baseline" gap={1.5}>
          <Text
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="800"
            color={colors.text}
            fontFamily="heading"
            lineHeight="1"
            css={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {tile.value}
          </Text>
          {tile.unit && (
            <Text fontSize="sm" fontWeight="600" color={colors.textMuted}>
              {tile.unit}
            </Text>
          )}
        </Flex>
        {tile.sub && (
          <Text fontSize="11px" color={colors.textMuted} mt={1.5}>
            {tile.sub}
          </Text>
        )}
      </Box>
    </MotionBox>
  )
}

export default function TripKpiGrid({ stats }: { stats: TripStats }) {
  const hasData = stats.pointCount > 1
  const tiles: Tile[] = [
    {
      label: 'Distancia total',
      value: hasData ? formatDistance(stats.totalDistanceM) : '-',
      icon: Route,
      accent: '#1A7A4C',
    },
    {
      label: 'Tiempo total',
      value: hasData ? formatDuration(stats.totalDurationMs) : '-',
      icon: Clock,
      accent: '#3d6fa8',
    },
    {
      label: 'Vel. promedio',
      value: hasData ? stats.avgSpeedKmh.toFixed(1) : '-',
      unit: hasData ? 'km/h' : undefined,
      sub: hasData ? 'en movimiento' : undefined,
      icon: Gauge,
      accent: '#1d6664',
    },
    {
      label: 'Vel. máxima',
      value: hasData ? stats.maxSpeedKmh.toFixed(1) : '-',
      unit: hasData ? 'km/h' : undefined,
      icon: Zap,
      accent: '#EAB308',
    },
    {
      label: 'Paradas',
      value: hasData ? String(stats.stopCount) : '-',
      sub: hasData && stats.stopCount > 0 ? 'detectadas en ruta' : undefined,
      icon: CircleStop,
      accent: '#c8442f',
    },
    {
      label: 'Tiempo detenido',
      value: hasData ? formatDuration(stats.stoppedDurationMs) : '-',
      icon: Pause,
      accent: '#b23a26',
    },
    {
      label: 'Tiempo en tráfico',
      value: hasData ? formatDuration(stats.slowDurationMs) : '-',
      sub: hasData ? '5–20 km/h' : undefined,
      icon: TrafficCone,
      accent: '#F97316',
    },
    {
      label: 'Desnivel',
      value: hasData && stats.maxAltitudeM != null ? `↑ ${Math.round(stats.elevationGainM)}` : '-',
      unit: hasData && stats.maxAltitudeM != null ? 'm' : undefined,
      sub:
        hasData && stats.maxAltitudeM != null
          ? `↓ ${Math.round(stats.elevationLossM)} m`
          : undefined,
      icon: Mountain,
      accent: '#8B5CF6',
    },
  ]

  return (
    <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
      {tiles.map((tile, i) => (
        <KpiTile key={tile.label} tile={tile} index={i} />
      ))}
    </Grid>
  )
}
